import { Select } from '../ui';
import { useAuth } from '../../auth/AuthContext';
import { useAssignableMembers } from '../../api/users';

/**
 * The "Assigned to" picker used wherever work can be owned by someone
 * (leads, tasks, follow-ups): Unassigned, Me, then every active teammate.
 * `value` is a tenant_user id or '' for unassigned. The new assignee (if it
 * isn't you) gets an "assigned to you" notification server-side.
 *
 * While the team list loads - or if it can't be loaded - the current value
 * and "Me" are still offered, so the control never shows a blank choice.
 */
export function AssigneeSelect({
  value,
  onChange,
  label = 'Assigned to',
  currentName,
  disabled,
  error,
  compact = false,
}: {
  value: string;
  onChange: (tenantUserId: string) => void;
  label?: string;
  /** Display name of the current assignee, for when they're not in the loaded list. */
  currentName?: string | null;
  disabled?: boolean;
  error?: string;
  /** Inline variant (no visible label) - e.g. inside a details table. */
  compact?: boolean;
}) {
  const { tenantUserId, user } = useAuth();
  const members = useAssignableMembers();

  const others = (members.data ?? []).filter((m) => m.tenantUserId !== tenantUserId);
  const known = new Set([tenantUserId, ...others.map((m) => m.tenantUserId)]);
  const options = [
    { value: '', label: 'Unassigned' },
    ...(tenantUserId ? [{ value: tenantUserId, label: `Me (${user?.displayName ?? 'you'})` }] : []),
    ...others.map((m) => ({
      value: m.tenantUserId,
      label: m.title ? `${m.displayName} - ${m.title}` : m.displayName,
    })),
    ...(value && !known.has(value) ? [{ value, label: currentName ?? 'Current assignee' }] : []),
  ];

  if (compact) {
    return (
      <select
        aria-label={label}
        className="ne-input"
        // Sized to its content - `.ne-input` is full-width, which squeezed the row's text.
        style={{ fontSize: 'var(--font-size-sm)', width: 'auto', maxWidth: 220 }}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <Select
      label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      options={options}
      error={error}
    />
  );
}
